import React, { useEffect, useMemo, useRef, useState } from "react";
import Input from "./ui/Input";
import "./ValidateCityInput.css";

// Cache in-module per evitare richieste duplicate
const cache = new Map();

// helpers robusti per insediamenti
function pickCity(address = {}) {
    return (
        address.city ||
        address.town ||
        address.village ||
        address.hamlet ||
        address.municipality ||
        address.locality ||
        null
    );
}
function isSettlementHit(hit) {
    if (!hit) return false;
    const klass = String(hit.class || "");
    const type = String(hit.type || "");
    const classType = `${klass}:${type}`.toLowerCase();
    if (/^place:(city|town|village|hamlet)$/.test(classType)) return true;

    const at = String(hit.addresstype || "").toLowerCase();
    if (/(city|town|village|hamlet|municipality|locality)/.test(at)) return true;

    if (pickCity(hit.address)) return true;
    return false;
}

export default function ValidatedCityInput({
    value,
    onChange,
    onValidityChange,
    onResolve, // opzionale: (hit) => void con il record completo di Nominatim
    label = "Città",
    placeholder = "Inserisci città",
    className = "",
    debounceMs = 400,
    countryCode = "", // es. "it"
    requireSettlement = true, // accetta solo insediamenti
    rightAdornment = null,
    suggestionsEnabled = true, // nuovo: puoi disattivare i suggerimenti
}) {
    const [isValid, setIsValid] = useState(null); // null | true | false
    const [checking, setChecking] = useState(false);

    // autocomplete state
    const [openSug, setOpenSug] = useState(false);
    const [sug, setSug] = useState([]); // lista risultati
    const [activeIdx, setActiveIdx] = useState(-1);

    // abort controller per validate e per suggest (li separo)
    const validateAbort = useRef(null);
    const suggestAbort = useRef(null);
    const rootRef = useRef(null);

    const q = useMemo(() => (value || "").trim(), [value]);

    /* ---------------- VALIDAZIONE ---------------- */
    useEffect(() => {
        if (!q || q.length < 2) {
            setIsValid(null);
            onValidityChange?.(null);
            return;
        }

        const doValidate = async () => {
            const key = `v:${countryCode}|${q.toLowerCase()}`;
            if (cache.has(key)) {
                const r = cache.get(key);
                setIsValid(r.ok);
                onValidityChange?.(r.ok);
                if (r.ok && r.hit) onResolve?.(r.hit);
                return;
            }

            if (validateAbort.current) validateAbort.current.abort();
            const controller = new AbortController();
            validateAbort.current = controller;

            try {
                setChecking(true);
                const params = new URLSearchParams({
                    format: "json",
                    q,
                    limit: "1",
                    addressdetails: "1",
                    "accept-language": "it",
                });
                if (countryCode) params.set("countrycodes", countryCode);

                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
                    {
                        signal: controller.signal,
                        headers: {
                            Accept: "application/json",
                            "User-Agent": "ALCWash/1.0 (support@alcwash.example)",
                        },
                    }
                );
                const data = await res.json();
                const hit = data?.[0];
                let ok = false;
                if (hit) {
                    const settlement = isSettlementHit(hit);
                    ok = requireSettlement ? settlement : Boolean(hit);
                }
                cache.set(key, { ok, hit: ok ? hit : null });
                setIsValid(ok);
                onValidityChange?.(ok);
                if (ok && hit) onResolve?.(hit);
            } catch (err) {
                if (err?.name !== "AbortError") {
                    console.error("City validation error", err);
                    setIsValid(null);
                    onValidityChange?.(null);
                }
            } finally {
                setChecking(false);
            }
        };

        const h = setTimeout(doValidate, debounceMs);
        return () => clearTimeout(h);
    }, [q, countryCode, debounceMs, onValidityChange, requireSettlement, onResolve]);

    /* ---------------- SUGGERIMENTI ---------------- */
    useEffect(() => {
        if (!suggestionsEnabled) return;
        if (!q || q.length < 2) {
            setSug([]);
            setOpenSug(false);
            setActiveIdx(-1);
            return;
        }

        const doSuggest = async () => {
            const key = `s:${countryCode}|${q.toLowerCase()}`;
            if (cache.has(key)) {
                setSug(cache.get(key) || []);
                setOpenSug(true);
                setActiveIdx(-1);
                return;
            }

            if (suggestAbort.current) suggestAbort.current.abort();
            const controller = new AbortController();
            suggestAbort.current = controller;

            try {
                const params = new URLSearchParams({
                    format: "json",
                    q,
                    limit: "7",
                    addressdetails: "1",
                    "accept-language": "it",
                });
                if (countryCode) params.set("countrycodes", countryCode);

                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
                    {
                        signal: controller.signal,
                        headers: {
                            Accept: "application/json",
                            "User-Agent": "ALCWash/1.0 (support@alcwash.example)",
                        },
                    }
                );
                let data = await res.json();
                // filtra se richiedi insediamenti
                if (requireSettlement) data = data.filter(isSettlementHit);

                // normalizza a 5-7 risultati max
                const list = (data || []).slice(0, 7);
                cache.set(key, list);
                setSug(list);
                setOpenSug(list.length > 0);
                setActiveIdx(-1);
            } catch (err) {
                if (err?.name !== "AbortError") {
                    console.error("City suggest error", err);
                    setSug([]);
                    setOpenSug(false);
                    setActiveIdx(-1);
                }
            }
        };

        const h = setTimeout(doSuggest, debounceMs);
        return () => clearTimeout(h);
    }, [q, countryCode, debounceMs, suggestionsEnabled, requireSettlement]);

    // chiusura menu su click fuori + ESC
    useEffect(() => {
        const onDoc = (e) => {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) setOpenSug(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpenSug(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    // selezione
    function commitSelection(hit) {
        const city =
            pickCity(hit.address) ||
            hit.name ||
            hit.display_name?.split(",")[0] ||
            "";
        if (!city) return;

        onChange(city);
        setIsValid(true);
        onValidityChange?.(true);
        onResolve?.(hit);
        setOpenSug(false);
    }

    // render riga suggerimento
    function renderItem(hit, idx) {
        const city = pickCity(hit.address) || hit.name || "—";
        const prov =
            hit.address?.state_district ||
            hit.address?.county ||
            hit.address?.state ||
            null;
        const cap = hit.address?.postcode || null;
        const subtitle = [prov, cap].filter(Boolean).join(" • ");

        return (
            <button
                key={`${hit.place_id}-${idx}`}
                type="button"
                className={`vc-item ${idx === activeIdx ? "active" : ""}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseLeave={() => setActiveIdx(-1)}
                onClick={() => commitSelection(hit)}
                role="option"
                aria-selected={idx === activeIdx}
            >
                <div className="vc-item-title">{city}</div>
                {subtitle ? <div className="vc-item-sub">{subtitle}</div> : null}
            </button>
        );
    }

    // gestione tastiera sul campo
    function onKeyDown(e) {
        if (!openSug || sug.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => (i + 1) % sug.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => (i - 1 + sug.length) % sug.length);
        } else if (e.key === "Enter") {
            if (activeIdx >= 0 && activeIdx < sug.length) {
                e.preventDefault();
                commitSelection(sug[activeIdx]);
            }
        }
    }

    return (
        <div ref={rootRef} className={`validated-city-input ${className}`} style={{ position: "relative" }}>
            <Input
                label={label}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(typeof e === "string" ? e : e.target.value)}
                onKeyDown={onKeyDown}
                className={isValid === false ? "input-error" : ""}
                right={
                    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        {checking ? <span aria-live="polite">⏳</span> : null}
                        {rightAdornment}
                    </span>
                }
                aria-autocomplete={suggestionsEnabled ? "list" : undefined}
                aria-expanded={openSug}
                aria-haspopup="listbox"
            />

            {/* feedback sotto al campo */}
            {checking && <span className="vc-hint">Controllo ortografia…</span>}
            {isValid === false && <span className="vc-error">Città non trovata</span>}
            {isValid === true && <span className="vc-ok">✓ Città riconosciuta</span>}

            {/* menu suggerimenti */}
            {openSug && sug.length > 0 && (
                <div className="vc-suggest" role="listbox">
                    {sug.map((hit, idx) => renderItem(hit, idx))}
                </div>
            )}
        </div>
    );
}